import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from '../components/home/HeroCarousel';
import Countdown from '../components/home/Countdown';
import HomeVideos from '../components/home/HomeVideos';
import ConsolationBanner from '../components/home/ConsolationBanner';
import { Ticket, ShieldCheck, Trophy, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { formatZAR } from '../lib/format';

const STEPS = [
  { icon: ShieldCheck, title: '1. Sign up', body: 'Create your account in under a minute — SA residents 18+ only.' },
  { icon: Ticket, title: '2. Buy R5 tickets', body: 'Grab one or many. The more you play, the better your odds.' },
  { icon: Trophy, title: '3. Friday 6pm draw', body: 'Winners announced live on our Previous Winners page.' },
];

export default function Home() {
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    api.get('/winners').then((r) => setWinners((r.data?.winners || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <HeroCarousel />
      <Countdown />

      {/* Consolation banner — sits between countdown and How it works */}
      <ConsolationBanner />

      {/* How it works */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">How it works</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl">
            <span className="text-gold-gradient">Three steps to your Golden Catch.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} data-testid={`home-step-${i}`} className="card-embossed p-8">
              <s.icon size={28} className="text-[#D4AF37]" />
              <h3 className="mt-6 font-serif-display text-2xl font-bold text-white">{s.title}</h3>
              <p className="mt-3 text-sm text-[#C0C0C0]/80 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Video sections */}
      <HomeVideos />

      {/* This week's prize */}
      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          data-testid="prize-card"
          className="card-embossed relative overflow-hidden p-10 sm:p-14"
        >
          <div className="absolute inset-0 opacity-30 pointer-events-none grain-overlay" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">This Week's Prize</p>
              <h3 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-black leading-none">
                <span className="shimmer-gold">{formatZAR(10000)}</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-[#C0C0C0]">
                One draw. One weekly winner. Every ticket brings you closer.
              </p>
            </div>
            <Link to="/buy-ticket" data-testid="prize-cta" className="btn-gold">
              Get Your Tickets
            </Link>
          </div>
        </div>
      </section>

      {/* Recent winners */}
      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Recent</p>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-black text-white">Winners' Circle</h2>
          </div>
          <Link to="/winners" className="text-sm text-[#D4AF37] hover:text-[#FFD700]">View all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {winners.length === 0
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card-embossed p-6 h-32 animate-pulse" />
              ))
            : winners.map((w) => (
                <div key={w.id} data-testid={`home-winner-${w.id}`} className="card-embossed p-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center text-black">
                      <Sparkles size={18} />
                    </div>
                    <div>
                      <p className="font-serif-display text-lg text-white font-bold">{w.name}</p>
                      <p className="text-xs text-[#C0C0C0]/70">{new Date(w.draw_date).toDateString()}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-2xl font-serif-display text-gold-gradient font-black">
                    {formatZAR(w.amount)}
                  </p>
                  <p className="text-xs text-[#C0C0C0]/70 mt-1">Held {w.ticket_count} tickets</p>
                </div>
              ))}
        </div>
      </section>
    </div>
  );
}
